import { useState, useRef } from 'react';
import { type Language, t } from '../i18n';

interface ContactCTAProps {
  language: Language;
  assessmentId?: string;
}

export function ContactCTA({ language, assessmentId }: ContactCTAProps) {
  const [description, setDescription] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      // Check file size (max 10MB)
      if (selectedFile.size > 10 * 1024 * 1024) {
        alert(language === 'it' ? 'File troppo grande (max 10MB)' : 'File too large (max 10MB)');
        return;
      }
      // Check file type
      if (selectedFile.type !== 'application/pdf') {
        alert(language === 'it' ? 'Solo file PDF sono accettati' : 'Only PDF files are accepted');
        return;
      }
      setFile(selectedFile);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!description.trim()) return;

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      // For now, just log the data - backend endpoint can be added later
      console.log('Contact request:', {
        assessmentId,
        description,
        fileName: file?.name,
        timestamp: new Date().toISOString(),
      });

      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 1000));

      setSubmitStatus('success');
      setDescription('');
      setFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="mt-8 pt-8 border-t border-border">
      <div className="text-center mb-6">
        <h2 className="text-xl font-bold text-foreground mb-2">
          {t(language, 'cta.title')}
        </h2>
        <p className="text-muted-foreground">
          {t(language, 'cta.subtitle')}
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Contact Form */}
        <div className="card p-4">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="cta-description" className="block text-sm font-medium text-foreground mb-2">
                {t(language, 'cta.form.description')}
              </label>
              <textarea
                id="cta-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={t(language, 'cta.form.descriptionPlaceholder')}
                rows={4}
                className="form-input w-full resize-none"
                required
              />
            </div>

            <div>
              <label htmlFor="cta-file" className="block text-sm font-medium text-foreground mb-2">
                {t(language, 'cta.form.upload')}
              </label>
              <div className="flex items-center gap-3">
                <input
                  ref={fileInputRef}
                  id="cta-file"
                  type="file"
                  accept=".pdf,application/pdf"
                  onChange={handleFileChange}
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="btn-secondary text-sm py-2 px-4"
                >
                  {language === 'it' ? 'Scegli file' : 'Choose file'}
                </button>
                <span className="text-sm text-muted-foreground">
                  {file ? file.name : t(language, 'cta.form.uploadLimit')}
                </span>
              </div>
            </div>

            {submitStatus === 'success' && (
              <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg text-sm text-green-600 dark:text-green-400">
                {t(language, 'cta.form.success')}
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-sm text-red-600 dark:text-red-400">
                {t(language, 'cta.form.error')}
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting || !description.trim()}
              className="btn-primary w-full py-2.5"
            >
              {isSubmitting
                ? (language === 'it' ? 'Invio in corso...' : 'Sending...')
                : t(language, 'cta.form.submit')}
            </button>
          </form>
        </div>

        {/* Calendar Placeholder */}
        <div className="card p-4">
          <div className="h-full flex flex-col">
            <h3 className="text-lg font-semibold text-foreground mb-2">
              {t(language, 'cta.calendar.title')}
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              {t(language, 'cta.calendar.subtitle')}
            </p>

            {/* Calendar Placeholder UI */}
            <div className="flex-1 border border-dashed border-border rounded-lg bg-muted/30 flex flex-col items-center justify-center p-6 text-center">
              {/* Calendar Icon */}
              <div className="w-16 h-16 mb-4 rounded-full bg-primary/10 flex items-center justify-center">
                <svg
                  className="w-8 h-8 text-primary"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
              </div>
              <p className="text-sm font-medium text-foreground mb-1">
                {t(language, 'cta.calendar.coming')}
              </p>
              <p className="text-xs text-muted-foreground">
                {t(language, 'cta.calendar.placeholder')}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
